
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { preloadCriticalRoutes } from './utils/performanceOptimizer';
import { initializePerformanceSystem } from './utils/masterPerformanceSystem';
import { toast } from '@/hooks/use-toast';

console.log("main.tsx: Starting application - Live view active:", new Date().toISOString());

// Enhanced error handling for early errors and module failures
window.addEventListener('error', (event) => {
  const e = event as ErrorEvent;
  const details = {
    message: e.message,
    src: e.filename,
    lineno: e.lineno,
    colno: e.colno,
  };
  console.error("Global error caught:", details, e.error);
  // Detect module script import failures and guide user
  if (
    typeof e.message === 'string' && (
      e.message.includes('Importing a module script failed') ||
      /Failed to (fetch dynamically imported module|load module script)/i.test(e.message)
    )
  ) {
    toast({
      title: "Update required",
      description: "A module failed to load. Please hard refresh (Ctrl/Cmd+Shift+R).",
      variant: "destructive",
    });
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const reason: any = event.reason;
  console.error("Unhandled promise rejection:", reason);
  const msg = typeof reason?.message === 'string' ? reason.message : String(reason);
  if (/Failed to fetch dynamically imported module|Importing a module script failed|load module script/i.test(msg)) {
    toast({
      title: "Module load failed",
      description: "Try a hard refresh (Ctrl/Cmd+Shift+R) and sign in again.",
      variant: "destructive",
    });
  }
});

try {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    console.error("Failed to find root element");
    document.body.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100vh; font-family: system-ui; padding: 2rem;">
        <div style="text-align: center; max-width: 500px;">
          <h1 style="color: #ef4444; margin-bottom: 1rem;">Application Error</h1>
          <p style="color: #6b7280; margin-bottom: 2rem;">Failed to find root element. This might be a browser compatibility issue.</p>
          <button onclick="window.location.reload()" style="padding: 0.5rem 1rem; background: #3b82f6; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Reload Page
          </button>
        </div>
      </div>
    `;
    throw new Error("Failed to find root element");
  }
  
  console.log("main.tsx: Root element found, creating React root");
  const root = createRoot(rootElement);
  
  console.log("main.tsx: Rendering App component");
  root.render(<App />);
  
  console.log("main.tsx: App rendered successfully");
  
  // Initialize performance optimizations
  try {
    initializePerformanceSystem();
    preloadCriticalRoutes();
  } catch (preloadError) {
    console.warn("Failed to initialize performance optimizations:", preloadError);
  }
} catch (error) {
  console.error("Error in main.tsx:", error);
  
  // Fallback error display
  document.body.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; height: 100vh; font-family: system-ui;">
      <div style="text-align: center; padding: 2rem; border: 1px solid #ccc; border-radius: 8px;">
        <h1>Application Error</h1>
        <p>Failed to start the application. Check the console for details.</p>
        <button onclick="window.location.reload()" style="margin-top: 1rem; padding: 0.5rem 1rem; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
          Reload Page
        </button>
      </div>
    </div>
  `;
}
